// TODO: switch to @wandb/weave/components/RadixTooltip once it exposes Arrow.
import * as Tooltip from '@radix-ui/react-tooltip';
import {Button, Icon, IconButton} from '@wandb/components';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React, {useEffect, useState} from 'react';
import {useHover} from 'react-aria';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import {isMountedInShell} from '../../shellRemote';
import type {UnseenSettledThread} from '../chat/threadReadStatus';
import {AGENT_NAME} from './constants';
import {WBAgentThinkingMark} from './WBAgentThinkingMark';

const BRAND_GRADIENT_ID = 'wbagent-brand-gradient';

// Outlined ARIA entry point in the Forge header, built on the standard ghost
// `Button`. The border is an inset shadow because the library's `Button` has no
// outlined variant; in forced-colors mode shadows are dropped, so a real border
// takes over. The gradient rule needs `!` to outrank the vendored BaseIcon
// selector, which triples its own class for (0, 3, 0) specificity.
const SHELL_BUTTON_CLASSES = clsx(
  'relative inline-flex',
  // Patina is not in the design system yet; cobalt-800 → patina-600 per the design.
  '[--patina-600:#70a484] [--wbagent-brand-gradient-start:var(--cobalt-800)] [--wbagent-brand-gradient-end:var(--patina-600)]',
  '[&_button]:shadow-[inset_0_0_0_var(--default-border-width)_var(--default-border-color)]',
  '[&_button[data-active]]:shadow-[inset_0_0_0_var(--default-border-width)_currentcolor]',
  '[@media(forced-colors:active)]:[&_button]:border-[length:var(--default-border-width)]',
  '[@media(forced-colors:active)]:[&_button]:border-solid',
  '[@media(forced-colors:active)]:[&_button]:border-[ButtonBorder]',
  // Written out literally so Tailwind's content scan finds the class; keep in
  // sync with BRAND_GRADIENT_ID.
  '[&_[data-icon=lightboard-solid]]:![--icon-color:url(#wbagent-brand-gradient)]',
  '[&_[data-icon=loading]]:animate-[spin_3s_linear_infinite]'
);

export const WBAgentChatButton = ({
  disableTooltip,
  isLoading,
  showIntroTooltip,
  hideIntroTooltip,
  unseenSettledThread,
  onImpression,
  onPress,
  ...props
}: AriaButtonProps & {
  disableTooltip?: boolean;
  isLoading?: boolean;
  showIntroTooltip?: boolean;
  hideIntroTooltip: () => void;
  unseenSettledThread: UnseenSettledThread | null;
  onImpression?: () => void;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isDisabled = props.isDisabled ?? false;
  const isExpanded =
    props['aria-expanded'] === true || props['aria-expanded'] === 'true';
  const isAriaShellHosted = isMountedInShell();
  // The shared `Button` does not take `onHoverStart`, so the hover-intent
  // preload listens on the wrapper around it instead.
  const {hoverProps} = useHover({
    isDisabled: !isAriaShellHosted,
    onHoverStart: props.onHoverStart,
  });
  // Forge hosts more than W&B, so the tooltip names what that surface calls
  // things: experiments rather than runs, and Forge rather than W&B.
  const tooltipSummary = isAriaShellHosted
    ? 'Analyze and visualize experiments, launch training, or ask any question about Forge'
    : 'Analyze and visualize runs, write code, launch experiments, or ask any W&B question';

  const dismissTooltip = () => {
    hideIntroTooltip();
    setIsTooltipOpen(false);
  };
  const closeIfPointerUntrackable = (event: React.PointerEvent) => {
    const next = event.nativeEvent.relatedTarget as Element | null;
    if (next == null || next.tagName === 'IFRAME') {
      setIsTooltipOpen(false);
    }
  };
  const handlePress: AriaButtonProps['onPress'] = event => {
    dismissTooltip();
    onPress?.(event);
  };

  // Fire once on mount — the button only renders when visible (i.e. the agent
  // is enabled for the user).
  useEffect(() => {
    onImpression?.();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unreadNotice = unseenSettledThread != null && (
    <div
      role="img"
      aria-label="Unread notice"
      className="pointer-events-none absolute -right-2 -top-5">
      <Icon icon="circle-filled" size="xs" color="red-500" />
    </div>
  );

  // In Forge the entry point is the shared `Button` so it picks up the same
  // rest/hover/active behavior as the neighboring header controls; only the
  // mark is branded (see SHELL_BUTTON_CLASSES). The `aria-expanded` type is
  // not on `ButtonProps`, so it rides along in a spread.
  const expandedAttrs: Record<`aria-${string}`, boolean | undefined> = {
    'aria-expanded': isExpanded,
  };
  const withTooltip = (trigger: React.ReactElement) =>
    disableTooltip || isDisabled ? (
      trigger
    ) : (
      <Tooltip.Provider delayDuration={0}>
        <Tooltip.Root
          open={showIntroTooltip || isTooltipOpen}
          onOpenChange={setIsTooltipOpen}>
          <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
          <Tooltip.Portal>
            <TooltipContent
              side="bottom"
              sideOffset={8}
              onPointerLeave={closeIfPointerUntrackable}
              className="relative z-[999999] max-w-[300px] rounded text-sm leading-[140%] shadow-md bg-wb-surface-inverse p-12 text-wb-text-inverse">
              <div>
                <div className="font-semibold text-wb-text-inverse">
                  {AGENT_NAME}: a coding agent for the AI loop
                </div>
                {showIntroTooltip && (
                  <div className="absolute right-8 top-8">
                    <IconButton
                      aria-label={`Dismiss ${AGENT_NAME} tooltip`}
                      icon="close"
                      size="sm"
                      variant="ghost"
                      onPress={dismissTooltip}
                    />
                  </div>
                )}
              </div>
              <div className="mt-4 text-wb-text-disabled">{tooltipSummary}</div>
              <Tooltip.Arrow className="fill-wb-surface-inverse" />
            </TooltipContent>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );

  const shellButton = (
    <TailwindContents>
      <span
        {...hoverProps}
        className={SHELL_BUTTON_CLASSES}
        onPointerLeave={closeIfPointerUntrackable}>
        <svg
          aria-hidden
          className="absolute h-0 w-0 overflow-hidden"
          focusable="false">
          <linearGradient id={BRAND_GRADIENT_ID} x1="0" y1="0" x2="1" y2="1">
            <stop
              offset="0"
              style={{stopColor: 'var(--wbagent-brand-gradient-start)'}}
            />
            <stop
              offset="1"
              style={{stopColor: 'var(--wbagent-brand-gradient-end)'}}
            />
          </linearGradient>
        </svg>
        {showIntroTooltip && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[5px] bg-[color-mix(in_srgb,var(--wb-accent-bg)_50%,transparent)] blur-[8px] dark:blur-[8px] motion-safe:animate-pulse motion-reduce:opacity-60"
          />
        )}
        {withTooltip(
          <Button
            aria-label={isLoading ? `${AGENT_NAME} (loading)` : AGENT_NAME}
            {...expandedAttrs}
            isDisabled={isDisabled}
            isActive={isExpanded}
            size="md"
            variant="ghost"
            startIcon={isLoading ? 'loading' : 'lightboard-solid'}
            onPress={handlePress}
            onPressStart={props.onPressStart}
            onFocus={props.onFocus}>
            {`Ask ${AGENT_NAME}`}
          </Button>
        )}
        {unreadNotice}
      </span>
    </TailwindContents>
  );

  const productButton = (
    <AriaButton
      aria-label={isLoading ? `${AGENT_NAME} (loading)` : AGENT_NAME}
      {...props}
      onPress={handlePress}
      onPointerLeave={closeIfPointerUntrackable}
      className="group relative rounded-full p-4 mr-8 disabled:cursor-default">
      {showIntroTooltip && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-4 rounded-[5px] bg-[color-mix(in_srgb,var(--wb-accent-bg)_50%,transparent)] blur-[8px] dark:blur-[8px] motion-safe:animate-pulse motion-reduce:opacity-60"
        />
      )}
      <div
        className={clsx(
          'relative inline-flex h-32 pl-12 pr-14 items-center gap-8 rounded-[5px] text-base font-semibold whitespace-nowrap outline outline-2 outline-transparent group-focus-visible:outline-wb-accent group-focus-visible:rounded-[9px] group-focus-visible:outline-offset-2 group-aria-[expanded=true]:border-wb-accent group-aria-[expanded=true]:bg-[color-mix(in_srgb,var(--wb-accent)_14%,transparent)]',
          'border border-moon-550 bg-white/[0.06] text-white',
          // When disabled the button keeps its pressed look and only drops the
          // hover treatment.
          !isDisabled &&
            'group-hover:border-moon-450 group-hover:bg-white/[0.11]',
          "font-['Source_Sans_3',sans-serif]"
        )}>
        <span className="inline-flex text-wb-accent">
          <WBAgentThinkingMark isActive={isLoading ?? false} size="md" />
        </span>
        Ask {AGENT_NAME}
      </div>
      {unreadNotice}
    </AriaButton>
  );

  return (
    <Tailwind>
      {isAriaShellHosted ? shellButton : withTooltip(productButton)}
    </Tailwind>
  );
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof Tooltip.Content>,
  React.ComponentPropsWithoutRef<typeof Tooltip.Content>
>((props, ref) => (
  <TailwindContents>
    <Tooltip.Content ref={ref} {...props} />
  </TailwindContents>
));
